import { getCookie } from "../../../utils/cookieUtils.js";
import { isNilOrEmptyString, isNilOrWhiteSpace, isString, type Nil } from "@mcwd/typescript-type-guards";
import type { IValueGetterWithDefault } from "./IValueGetterWithDefault.js";

export abstract class CookiePersistedValue<
  T,
  TCalculate extends T | Nil,
  TDefault extends T | Nil = undefined
> implements IValueGetterWithDefault<T, TDefault>
{
  protected __valueStore: { value?: Exclude<TCalculate, Nil> | TDefault } = {};
  protected __existingCookieTakesPriority: boolean;
  #cookieName: string;
  #defaultValue: TDefault;
  constructor ({ cookieName, existingCookieTakesPriority, defaultValue }: ({ cookieName: string, existingCookieTakesPriority: boolean, defaultValue: TDefault }))
  {
    this.#cookieName = cookieName;
    this.__existingCookieTakesPriority = existingCookieTakesPriority;
    this.#defaultValue = defaultValue;
  }

  get CookieName ()
  {
    return this.#cookieName;
  }

  abstract parseCookieValue (cookieString: string): TCalculate | Nil

  getValueFromCookie (): TCalculate | Nil {
    const cookieString = getCookie(this.#cookieName, undefined);
    if (isString(cookieString)){
      return this.parseCookieValue(cookieString);
    }
    return undefined;
  }

  get defaultValueIfEmpty() {
    return this.#defaultValue as TDefault;
  }

  protected abstract __calculateValue (): TCalculate;

  #calculateValueByCookiePriority (): TCalculate | Nil
  {
    if (this.__existingCookieTakesPriority) {
      const cookieRes = this.getValueFromCookie();
      return isNilOrWhiteSpace(cookieRes) ? this.__calculateValue() : cookieRes;
    }
    const calcRes = this.__calculateValue();
    return isNilOrWhiteSpace(calcRes) ? this.getValueFromCookie() : calcRes;
  }

  calculateValue (): Exclude<TCalculate, Nil> | TDefault
  {
    const origResult = this.#calculateValueByCookiePriority();

    const result = isNilOrEmptyString(origResult)
      ? this.defaultValueIfEmpty
      : (origResult as Exclude<TCalculate, Nil>);
    this.__valueStore.value = result;
    return result;
  }


  get value (): Exclude<TCalculate, Nil> | TDefault
  {
    const valueStore = this.__valueStore;
    if ("value" in valueStore)
    {
      const val = valueStore.value as Exclude<TCalculate, Nil> | TDefault;
      return val;
    }
    return this.calculateValue();
  }
}